import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { SourceStatusEnum } from '@am3nd-org/pointcraft-types';

import { MOCK_TRANSACTIONS } from '../mock';
import { INPUT_LIMITS } from '../constants';
import { storeEvents, STORE_EVENTS, sanitizeSearchQuery } from '../utils';
import type { Transaction, TransactionType, TransactionStatus, TransactionFilters } from '../types';

/* =================== TRANSACTION STORE STATE =================== */
interface TransactionState {
  transactions: Transaction[];
  isLoading: boolean;
  error: string | null;
  pageSize: number;
  setPageSize: (pageSize: number) => void;

  // CRUD OPERATIONS
  getTransactions: () => Transaction[];
  getTransactionsPaginated: (
    page: number,
    pageSize?: number
  ) => {
    data: Transaction[];
    total: number;
    totalPages: number;
  };

  getTransactionById: (id: string) => Transaction | undefined;
  createTransaction: (transaction: Omit<Transaction, 'id'>) => Transaction;
  updateTransaction: (id: string, updates: Partial<Transaction>) => void;
  deleteTransaction: (id: string) => void;

  // FILTERING & SEARCHING
  filterTransactions: (filters: TransactionFilters) => Transaction[];
  searchTransactions: (query: string) => Transaction[];
  getTransactionsByCustomer: (customerId: string) => Transaction[];
  getTransactionsByStatus: (status: TransactionStatus) => Transaction[];
  getTransactionsByType: (type: TransactionType) => Transaction[];
  getTransactionsByDateRange: (start: Date, end: Date) => Transaction[];

  // Analytics
  getTotalRevenue: () => number;
  getRevenueByDateRange: (start: Date, end: Date) => number;
  getTransactionStats: (
    start?: Date,
    end?: Date
  ) => {
    total: number;
    completed: number;
    pending: number;
    failed: number;
    refunded: number;
  };

  // UTILITY
  refreshTransactions: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

/* =================== TRANSACTION STORE =================== */
export const useTransactionStore = create<TransactionState>()(
  devtools(
    (set, get) => ({
      transactions: process.env.NODE_ENV === 'development' ? [...MOCK_TRANSACTIONS] : [],
      isLoading: false,
      error: null,
      pageSize: INPUT_LIMITS.STORE_DEFAULT_PAGE_SIZE,
      setPageSize: (pageSize) => set({ pageSize }),

      // CRUD OPERATIONS
      getTransactions: () => get().transactions,

      getTransactionsPaginated: (page, customPageSize) => {
        const { pageSize, transactions } = get();
        const size = customPageSize || pageSize;
        const total = transactions.length;
        const totalPages = Math.ceil(total / size);
        const start = (page - 1) * size;
        const end = start + size;
        const data = transactions.slice(start, end);
        return {
          data,
          total,
          totalPages,
        };
      },
      getTransactionById: (id: string) => get().transactions.find((t) => t.id === id),

      createTransaction: (transaction) => {
        const newTransaction: Transaction = {
          ...transaction,
          id: `txn_${crypto.randomUUID() as string}`,
        };
        set((state) => ({
          transactions: [newTransaction, ...state.transactions],
        }));

        // EMIT EVENT FOR NEW TRANSACTION
        storeEvents.emit(STORE_EVENTS.TRANSACTION_CREATED, newTransaction);

        return newTransaction;
      },

      updateTransaction: (id, updates) => {
        set((state) => ({
          transactions: state.transactions.map((t) => (t.id === id ? { ...t, ...updates } : t)),
        }));

        // EMIT EVENT FOR UPDATED TRANSACTION
        const updatedTransaction = get().getTransactionById(id);
        if (updatedTransaction) {
          storeEvents.emit(STORE_EVENTS.TRANSACTION_UPDATED, updatedTransaction);
        }
      },

      deleteTransaction: (id) => {
        const deletedTransaction = get().getTransactionById(id);

        set((state) => ({
          transactions: state.transactions.filter((transaction) => transaction.id !== id),
        }));

        // EMIT EVENT FOR DELETED TRANSACTION
        if (deletedTransaction) {
          storeEvents.emit(STORE_EVENTS.TRANSACTION_DELETED, deletedTransaction);
        }
      },

      // FILTERING & SEARCHING
      filterTransactions: (filters) => {
        const { status, type, customerId, startDate, endDate, minAmount, maxAmount } = filters;

        // SINGLE-PASS FILTERING FOR BETTER PERFORMANCE
        return get().transactions.filter((transaction) => {
          if (status && transaction.status !== status) return false;
          if (type && transaction.type !== type) return false;
          if (customerId && transaction.customerId !== customerId) return false;
          if (startDate && transaction.date < startDate) return false;
          if (endDate && transaction.date > endDate) return false;
          if (minAmount !== undefined && transaction.amount < minAmount) return false;
          if (maxAmount !== undefined && transaction.amount > maxAmount) return false;
          return true;
        });
      },

      searchTransactions: (query) => {
        // SANITIZE SEARCH QUERY TO PREVENT XSS AND INJECTION ATTACKS
        const sanitizedQuery = sanitizeSearchQuery(query);
        if (!sanitizedQuery) return [];

        const lowerQuery = sanitizedQuery.toLowerCase();
        return get().transactions.filter(
          (transaction) =>
            transaction.id.toLowerCase().includes(lowerQuery) ||
            transaction.description?.toLowerCase().includes(lowerQuery) ||
            transaction.paymentMethod.toLowerCase().includes(lowerQuery) ||
            transaction.amount.toString().includes(lowerQuery)
        );
      },

      getTransactionsByCustomer: (customerId) =>
        get().transactions.filter((transaction) => transaction.customerId === customerId),

      getTransactionsByStatus: (status) =>
        get().transactions.filter((transaction) => transaction.status === status),

      getTransactionsByType: (type) =>
        get().transactions.filter((transaction) => transaction.type === type),

      getTransactionsByDateRange: (start, end) =>
        get().transactions.filter(
          (transaction) => transaction.date >= start && transaction.date <= end
        ),

      // ANALYTICS
      getTotalRevenue: () =>
        get()
          .transactions.filter(
            (transaction) =>
              (transaction.status === SourceStatusEnum.PAID ||
                transaction.status === SourceStatusEnum.OVERPAID) &&
              transaction.type === 'payment'
          )
          .reduce((sum, transaction) => sum + transaction.amount, 0),

      getRevenueByDateRange: (start, end) =>
        get()
          .transactions.filter(
            (transaction) =>
              (transaction.status === SourceStatusEnum.PAID ||
                transaction.status === SourceStatusEnum.OVERPAID) &&
              transaction.type === 'payment' &&
              transaction.date >= start &&
              transaction.date <= end
          )
          .reduce((sum, transaction) => sum + transaction.amount, 0),

      getTransactionStats: (start?: Date, end?: Date) => {
        const { transactions } = get();

        // SINGLE-PASS AGGREGATION FOR BETTER PERFORMANCE
        const stats = transactions.reduce(
          (acc, transaction) => {
            // FILTER BY DATE RANGE IF PROVIDED
            if (start && end && (transaction.date < start || transaction.date > end)) {
              return acc;
            }

            // COUNT TOTAL
            acc.total += 1;

            // COUNT BY STATUS (grouped by category)
            switch (transaction.status) {
              // Success states -> completed
              case SourceStatusEnum.PAID:
              case SourceStatusEnum.OVERPAID:
                acc.completed += 1;
                break;
              // Pending states -> pending
              case SourceStatusEnum.UNPAID:
              case SourceStatusEnum.PARTIAL:
              case SourceStatusEnum.OVERDUE:
                acc.pending += 1;
                break;
              // Failed states -> failed
              case SourceStatusEnum.CANCELLED:
              case SourceStatusEnum.FAILED_FINAL:
                acc.failed += 1;
                break;
              // Refund states -> refunded
              case SourceStatusEnum.REFUNDED:
              case SourceStatusEnum.PARTIALLY_REFUNDED:
                acc.refunded += 1;
                break;
              default:
                break;
            }

            return acc;
          },
          {
            total: 0,
            completed: 0,
            pending: 0,
            failed: 0,
            refunded: 0,
          }
        );

        return stats;
      },

      // UTILITY
      refreshTransactions: () => {
        // DEEP CLONE TO PREVENT MUTATIONS AND ENSURE DATES ARE DATE OBJECTS
        const clonedTransactions =
          process.env.NODE_ENV === 'development'
            ? MOCK_TRANSACTIONS.map((transaction) => ({
                ...transaction,
                date: new Date(transaction.date),
              }))
            : [];
        set({ transactions: clonedTransactions });
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),
    }),
    { name: 'transaction-store' }
  )
);
